/**
 * atomberg-moengage.js
 * ─────────────────────
 * Thin wrapper around the MoEngage Web SDK (loaded via the MoEngage Shopify
 * app embed, which exposes window.Moengage).
 *
 * Responsibilities:
 *   1. Last-touch attribution — persist URL utm and gclid params to lt_ keys in
 *      localStorage (mirrors the SSR app's lastTouchAttribution()), so the keys
 *      survive across the session and later pages.
 *   2. Auto-enrich every event with those attribution params (read from lt_*,
 *      emitted unprefixed) so callers never repeat the boilerplate.
 *   3. Queue events fired before the SDK finishes initialising and flush them
 *      once window.Moengage.track_event exists.
 *   4. Tag every event with AB_TEST_VARIANT below — see atomberg-gtm-datalayer.js
 *      for the GTM side.
 *
 * Usage (this file loads once globally in theme.liquid; call from any script):
 *   AtombergMoe.track('view_category', { category_name: 'Ceiling Fans' });
 *   AtombergMoe.setAttributes({ consent_status: 'given' });
 */
(function () {
  /** @type {any} */
  var w = window;
  if (w._atombergMoeInit) return;
  w._atombergMoeInit = true;

  // Order mirrors the SSR app's gtmParameters(); these are the event-payload
  // keys. localStorage keys are the same names prefixed with `lt_`.
  var UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'gclid', 'utm_term', 'utm_content'];

  // Generic, reusable A/B rollout flag — not scoped to any one feature by name,
  // since several unrelated rollouts (e.g. the warranty flow's own separate
  // warranty_variant) can be live at once and this doesn't need to tell them
  // apart, just mark the session as "in a treatment". Always literally 'a'
  // (control) or 'b' (treatment), so every future rollout reads the same way
  // in GTM/MoEngage without new custom-dimension setup each time. Merged into
  // every tracked event here the same way getUtm() is, rather than at each
  // individual track() call site. See docs/components/atomberg-customer-images/
  // README.md's "A/B tracking" section, and keep this in sync with the
  // identical constant in atomberg-gtm-datalayer.js.
  var AB_TEST_VARIANT = 'a';

  /**
   * Last-touch attribution: if the current URL carries any tracking param,
   * overwrite ALL lt_* keys from the URL (absent ones cleared). If the URL has
   * none, existing lt_* values persist untouched — last touch wins.
   */
  function captureAttribution() {
    try {
      var sp = new URLSearchParams(w.location.search);
      var hasAny = UTM_KEYS.some(function (k) {
        return sp.has(k);
      });
      if (!hasAny) return;
      UTM_KEYS.forEach(function (k) {
        localStorage.removeItem('lt_' + k);
        var v = sp.get(k);
        if (v) localStorage.setItem('lt_' + k, v);
      });
    } catch (_) {}
  }

  /** @returns {Record<string, string>} All 6 attribution keys, '' when absent. */
  function getUtm() {
    /** @type {Record<string, string>} */
    var out = {};
    UTM_KEYS.forEach(function (k) {
      var v = '';
      try {
        v = localStorage.getItem('lt_' + k) || '';
      } catch (_) {}
      out[k] = v;
    });
    return out;
  }

  // Each queued op is a track_event, a standard-attribute batch (setUser), or
  // a custom-attribute batch (setAttributes). FIFO, so an identify queued
  // before custom attributes always lands on the right profile.
  /** @type {Array<{kind: 'event', name: string, props: Record<string, any>} | {kind: 'user' | 'custom', attrs: Record<string, string>}>} */
  var queue = [];

  /** @returns {boolean} */
  function sdkReady() {
    return !!(w.Moengage && typeof w.Moengage.track_event === 'function');
  }

  // MoEngage attribute key -> SDK setter method name.
  /** @type {Record<string, string>} */
  var USER_SETTERS = {
    id: 'add_unique_user_id',
    firstName: 'add_first_name',
    lastName: 'add_last_name',
    email: 'add_email',
    mobile: 'add_mobile',
  };

  function flush() {
    if (!sdkReady()) return;
    var sdk = w.Moengage;
    var pending = queue.splice(0);
    pending.forEach(function (op) {
      if (op.kind === 'event') {
        sdk.track_event(op.name, op.props);
        return;
      }
      if (op.kind === 'custom') {
        if (typeof sdk.add_user_attribute !== 'function') return;
        Object.keys(op.attrs).forEach(function (key) {
          sdk.add_user_attribute(key, op.attrs[key]);
        });
        return;
      }
      Object.keys(op.attrs).forEach(function (key) {
        var setter = USER_SETTERS[key];
        if (setter && typeof sdk[setter] === 'function') sdk[setter](op.attrs[key]);
      });
    });
  }

  /**
   * Track a MoEngage event. Attribution params are merged automatically.
   * Safe to call before the SDK loads — queued and flushed once ready.
   * @param {string} name - MoEngage event name (exact casing from the spec)
   * @param {Record<string, any>} [props] - Event-specific properties
   */
  function track(name, props) {
    queue.push({
      kind: 'event',
      name: name,
      props: Object.assign({}, getUtm(), { ab_test_variant: AB_TEST_VARIANT }, props || {}),
    });
    flush();
  }

  /**
   * Normalize an Indian mobile number to E.164 (+91XXXXXXXXXX). MoEngage
   * campaigns (SMS/WhatsApp) silently fail delivery on a bare 10-digit
   * number — it needs the country code. Callers across the codebase pass
   * setUser id/mobile in several raw shapes (bare 10-digit, 91-prefixed
   * with no +, already-correct +91) depending on the source (Shopify
   * customer.phone, a typed OTP field, Magic Checkout's own phone field) —
   * centralizing the fix here means every current and future setUser call
   * gets it right without each caller needing to remember to format it.
   * Only transforms values that look like an Indian mobile (10 digits
   * starting 6-9, optionally already prefixed with 91 or +91) — anything
   * else (an email, e.g. magic:user_data's id falls back to email when
   * phone is absent, or an unrecognized shape) passes through unchanged so
   * this can never corrupt a non-phone identifier.
   * @param {string} raw
   * @returns {string}
   */
  function toIndianE164(raw) {
    var value = String(raw || '');
    if (!value || value.indexOf('@') !== -1) return value;
    var digits = value.replace(/\D/g, '');
    if (digits.length > 10 && digits.indexOf('91') === 0) digits = digits.slice(-10);
    if (!/^[6-9]\d{9}$/.test(digits)) return value;
    return '+91' + digits;
  }

  /**
   * Set MoEngage user attributes (the "User Attributes" spec event). Only keys
   * with a non-empty value are sent. id/mobile are normalized to E.164 (see
   * toIndianE164) since MoEngage campaigns need the country code. Safe to
   * call before the SDK loads.
   * @param {{id?: string, firstName?: string, lastName?: string, email?: string, mobile?: string}} attrs
   */
  function setUser(attrs) {
    /** @type {Record<string, string>} */
    var src = /** @type {any} */ (attrs || {});
    /** @type {Record<string, string>} */
    var clean = {};
    Object.keys(src).forEach(function (k) {
      if (!src[k]) return;
      clean[k] = k === 'id' || k === 'mobile' ? toIndianE164(src[k]) : src[k];
    });
    if (!Object.keys(clean).length) return;
    queue.push({ kind: 'user', attrs: clean });
    flush();
  }

  /**
   * Set custom MoEngage user attributes (add_user_attribute) — anything
   * outside setUser's standard fields. Only keys with a non-empty value are
   * sent. Safe to call before the SDK loads.
   * @param {Record<string, string>} attrs
   */
  function setAttributes(attrs) {
    /** @type {Record<string, string>} */
    var clean = {};
    Object.keys(attrs || {}).forEach(function (k) {
      if (attrs[k]) clean[k] = attrs[k];
    });
    if (!Object.keys(clean).length) return;
    queue.push({ kind: 'custom', attrs: clean });
    flush();
  }

  // The app embed initialises the SDK asynchronously and exposes no reliable
  // ready event, so poll briefly until window.Moengage is live, then stop.
  var tries = 0;
  var poll = setInterval(function () {
    tries += 1;
    if (sdkReady()) {
      flush();
      clearInterval(poll);
    } else if (tries >= 50) {
      // ~10s ceiling — give up polling; track() still flushes if the SDK
      // appears later (e.g. a subsequent event fires after init).
      clearInterval(poll);
    }
  }, 200);

  captureAttribution();

  w.AtombergMoe = { track: track, setUser: setUser, setAttributes: setAttributes };
})();
